import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import type { Company, Industry } from "@/types/auth";
import type { ApiError } from "@/types/auth";
import { useToast } from "@/hooks/use-toast";
import { fetchIndustries } from "@/api/products";
import { CompanyBasicInfo } from "./company-form/CompanyBasicInfo";
import { CompanyTypeSelect } from "./company-form/CompanyTypeSelect";
import { IndustrySelect } from "./company-form/IndustrySelect";
import { ReferralSourceSelect } from "./company-form/ReferralSourceSelect";
import { Loader2 } from "lucide-react";

const companySchema = z.object({
  name: z.string().min(1, "Company name is required"),
  industry: z.array(z.number()).min(1, "At least one industry must be selected"),
  referral_source: z.enum(["FRIEND", "SOCIAL_MEDIA", "SEARCH_ENGINE", "OTHER"]),
  company_type: z.enum(["ECOMMERCE", "RETAILER", "BOTH"]),
  number_of_products: z.number().min(1, "Number of products is required"),
  size: z.number().min(1, "Company size is required"),
  other_referral_source: z.string().optional(),
});

interface CompanyFormProps {
  onSubmit: (data: Company) => Promise<{ success: boolean }>;
  apiError?: ApiError['error'];
}

export function CompanyForm({ onSubmit, apiError }: CompanyFormProps) {
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const form = useForm<Company>({
    resolver: zodResolver(companySchema),
    defaultValues: {
      industry: [],
      referral_source: "FRIEND",
      company_type: "RETAILER",
      number_of_products: 0,
      size: 0,
    },
  });

  useEffect(() => {
    const loadIndustries = async () => {
      try {
        const data = await fetchIndustries();
        setIndustries(data);
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load industries. Please try again.",
        });
      }
    };

    loadIndustries();
  }, []);

  useEffect(() => {
    if (apiError?.detail) {
      Object.entries(apiError.detail).forEach(([field, errors]) => {
        form.setError(field as keyof Company, {
          type: 'server',
          message: Array.isArray(errors) ? errors[0] : errors as string
        });
      });
    }
  }, [apiError, form]);

  const handleSubmit = async (data: Company) => {
    setIsSubmitting(true);
    try {
      const formattedData = {
        ...data,
        number_of_products: Number(data.number_of_products),
        size: Number(data.size),
      };
      await onSubmit(formattedData);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <CompanyBasicInfo form={form} isSubmitting={isSubmitting} apiError={apiError?.detail} />
        <IndustrySelect form={form} industries={industries} isSubmitting={isSubmitting} apiError={apiError?.detail} />
        <CompanyTypeSelect form={form} isSubmitting={isSubmitting} apiError={apiError?.detail} />
        <ReferralSourceSelect form={form} isSubmitting={isSubmitting} apiError={apiError?.detail} />

        <Button 
          type="submit" 
          className="w-full"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Setting up...
            </>
          ) : (
            "Complete Setup"
          )}
        </Button>
      </form>
    </Form>
  );
}